import type { Vendor } from '@shopgnd/shared';
import type { PlanId } from '@/lib/vendorPlans';
import { getRuntimePlanDefinitions } from '@/lib/platformPlanConfig';
import { getPlanDefinition } from '@shopgnd/shared';

export type PlatformSettingsFees = {
  convenienceFeeBps: number;
  defaultCommissionBps: number;
  starterCommissionBps: number;
  growthCommissionBps: number;
  proCommissionBps: number;
};

export const DEFAULT_PLATFORM_SETTINGS: PlatformSettingsFees = {
  convenienceFeeBps: 200,
  defaultCommissionBps: 800,
  starterCommissionBps: 1000,
  growthCommissionBps: 800,
  proCommissionBps: 600,
};

let cachedSettings: PlatformSettingsFees = { ...DEFAULT_PLATFORM_SETTINGS };

export function applyPlatformFeeSettings(patch: Partial<PlatformSettingsFees>): void {
  cachedSettings = { ...cachedSettings, ...patch };
}

export function getPlatformFeeSettings(): PlatformSettingsFees {
  return cachedSettings;
}

export function formatFeePercent(bps: number): string {
  const pct = bps / 100;
  return pct % 1 === 0 ? `${pct.toFixed(0)}%` : `${pct.toFixed(1)}%`;
}

export function serviceFeeLabel(bps?: number): string {
  return `Service fee (${formatFeePercent(bps ?? cachedSettings.convenienceFeeBps)})`;
}

export function platformConvenienceFeeCents(
  subtotalCents: number,
  bps = cachedSettings.convenienceFeeBps,
): number {
  if (subtotalCents <= 0) return 0;
  return Math.round((subtotalCents * bps) / 10_000);
}

export function vendorCommissionCents(merchandiseCents: number, commissionBps: number): number {
  if (merchandiseCents <= 0 || commissionBps <= 0) return 0;
  return Math.round((merchandiseCents * commissionBps) / 10_000);
}

export function commissionBpsForPlan(planId: PlanId | undefined, settings = cachedSettings): number {
  const fromPlan = getPlanDefinition(planId, getRuntimePlanDefinitions()).commissionBps;
  if (fromPlan > 0) return fromPlan;
  if (planId === 'starter') return settings.starterCommissionBps;
  if (planId === 'growth') return settings.growthCommissionBps;
  if (planId === 'pro') return settings.proCommissionBps;
  return settings.defaultCommissionBps;
}

export function commissionBpsForVendor(vendor: Pick<Vendor, 'commissionBps' | 'subscriptionPlanId'>): number {
  if (vendor.commissionBps != null && vendor.commissionBps > 0) return vendor.commissionBps;
  const planId = vendor.subscriptionPlanId as PlanId | undefined;
  if (planId) return commissionBpsForPlan(planId);
  const plan = getPlanDefinition(planId, getRuntimePlanDefinitions());
  return plan.commissionBps;
}

/** @deprecated use platformConvenienceFeeCents */
export const PLATFORM_CONVENIENCE_FEE_BPS = DEFAULT_PLATFORM_SETTINGS.convenienceFeeBps;
